import { createFocusTrap } from 'focus-trap';
import { add, listen, qs, qsa, remove, toggle } from '@fluorescent/dom';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';

import { updateFilters } from '@/lib/filters/events';
import priceRange from '@/lib/filters/price-range';
import { on } from '@/glow/events';

const sel = {
  filter: '[data-filter]',
  filterTarget: '[data-filter-target]',
  flyouts: '[data-filter-modal]',
  button: '[data-button]',
  wash: '[data-drawer-wash]',
  sort: '[data-sort]',
  close: '[data-close-icon]',
  group: '.filter-drawer__group',
  panel: '.filter-drawer__panel',
  flyoutWrapper: '[data-filer-modal-wrapper]',
  priceRange: '[data-price-range]',
};

const classes = {
  active: 'active',
  activeFilters: 'filters-active',
  fixed: 'is-fixed',
};

const filterDrawer = node => {
  const flyouts = qsa(sel.flyouts, node);
  const flyoutContainer = qs(sel.flyoutWrapper, node);
  const wash = qs(sel.wash, node);
  const filters = qsa(sel.filter, node);
  const sortMethods = qsa(`[data-filter-modal="__sort"] ${sel.sort}`);
  const rangeInputs = qsa('[data-range-input]', node);
  let focusTrap = null;
  let range = null;
  const rangeContainer = qs(sel.priceRange, node);

  if (rangeContainer) {
    range = priceRange(rangeContainer);
  }

  const events = [
    listen(qsa(sel.filterTarget, node), 'click', clickFlyoutTrigger),
    listen(filters, 'click', clickFilter),
    listen(sortMethods, 'click', clickSort),
    listen(wash, 'click', clickWash),
    listen(qsa(sel.button, node), 'click', clickButton),
    listen(qsa(sel.close, node), 'click', clickWash),
    listen(node, 'keydown', ({ keyCode }) => {
      if (keyCode === 27) clickWash();
    }),
    listen(rangeInputs, 'change', rangeChanged),
    on('filters:filter-removed', () => syncActiveStates()),
  ];

  const mobiletrigger = qs('[data-mobile-trigger]', node);
  const mobileFilters = qs('[data-mobile-filters]', node);
  if (mobileFilters || mobiletrigger) {
    events.push(
      listen(mobiletrigger, 'click', () => {
        mobileFilters.style.setProperty(
          '--mobile-filters-offset',
          `${mobileFilters.clientHeight - 20}px`
        );
        toggle(mobileFilters, classes.active);
      })
    );
  }

  setActiveStates();

  function setActiveStates() {
    const panels = qsa(sel.panel, node);

    panels.forEach(panel => {
      const groups = qsa(sel.group, panel);

      if (groups.length) {
        groups.forEach(group => {
          if (containsCheckedInputs(qsa('input', group))) {
            add(group, classes.activeFilters);
          }
        });
      }

      toggle(
        panel,
        classes.activeFilters,
        containsCheckedInputs(qsa('input', panel))
      );
    });
  }

  function clickFlyoutTrigger(e) {
    e.preventDefault();
    const { filterTarget } = e.currentTarget.dataset;
    const modal = qs(`[data-filter-modal="${filterTarget}"]`, node);
    focusTrap = createFocusTrap(modal, { allowOutsideClick: true });

    add(flyoutContainer, classes.fixed);

    setTimeout(() => {
      add(flyoutContainer, classes.active);
      add(modal, classes.active);
    }, 50);

    modal.setAttribute('aria-hidden', 'false');
    focusTrap.activate();
    disableBodyScroll(node, {
      allowTouchMove: el => {
        while (el && el !== document.body) {
          if (el.getAttribute('data-scroll-lock-ignore') !== null) {
            return true;
          }
          el = el.parentNode;
        }
      },
      reserveScrollBarGap: true,
    });
  }

  function clickWash(e) {
    e && e.preventDefault();

    // Blur any focused element inside the drawer BEFORE setting aria-hidden.
    // Chrome's accessibility engine crashes when aria-hidden is set on an
    // ancestor of the currently-focused element.
    if (document.activeElement && node.contains(document.activeElement)) {
      document.activeElement.blur();
    }

    focusTrap && focusTrap.deactivate();

    remove(flyouts, classes.active);
    remove(flyoutContainer, classes.active);
    flyouts.forEach(flyout => flyout.setAttribute('aria-hidden', 'true'));
    enableBodyScroll(node);

    setTimeout(() => {
      remove(flyoutContainer, classes.fixed);
    }, 300);
  }

  function clickFilter(e) {
    checkForActiveModalitems(e.currentTarget);

    const wrappingContainer = e.target.closest(sel.group);

    wrappingContainer &&
      toggle(
        wrappingContainer,
        classes.activeFilters,
        containsCheckedInputs(qsa('input', wrappingContainer))
      );
  }

  function clickSort(e) {
    checkForActiveModalitems(e.target);
  }

  function rangeChanged(e) {
    checkForActiveModalitems(e.currentTarget);

    const wrappingContainer = e.target.closest(sel.group);

    wrappingContainer &&
      toggle(wrappingContainer, classes.activeFilters, rangeInputsHaveValue());
  }

  function clickButton(e) {
    e.preventDefault();
    const { button } = e.currentTarget.dataset;

    const scope = e.currentTarget.closest(sel.flyouts);
    const { filterModal } = scope.dataset;

    // Sort flyouts
    if (filterModal === '__sort') {
      if (button === 'clear-all') {
        sortMethods.forEach(element => {
          qs('input', element).checked = false;
        });
        remove(e.currentTarget.closest(sel.panel), classes.activeFilters);
      }

      if (button === 'apply') {
        updateFilters();
        clickWash();
        return;
      }
    } else {
      // Regular filter flyout

      if (button === 'clear-all') {
        qsa('input', scope).forEach(input => {
          input.checked = false;
        });

        const panel = e.currentTarget.closest(sel.panel);
        remove([...qsa(sel.group, panel), panel], classes.activeFilters);
        range && range.reset();
      }

      if (button === 'clear') {
        const wrappingContainer = e.target.closest(sel.group);
        qsa('input', wrappingContainer).forEach(input => {
          input.checked = false;
        });
        remove(e.currentTarget.closest(sel.group), classes.activeFilters);

        checkForActiveModalitems(e.currentTarget);

        if (qs('.filter-drawer__price-range', wrappingContainer)) {
          range.reset();
        }
      }

      if (button === 'apply') {
        range && range.validateRange();
        updateFilters();

        clickWash();
      }
    }
  }

  function containsCheckedInputs(items) {
    let isActive = false;

    items.forEach(input => {
      if (input.checked) {
        isActive = true;
      }
    });

    return isActive;
  }

  function rangeInputsHaveValue() {
    let hasValue = false;

    rangeInputs.forEach(input => {
      if (input.value !== '') hasValue = true;
    });

    return hasValue;
  }

  function checkForActiveModalitems(currentTarget) {
    const panel = currentTarget.closest(sel.panel);

    if (!panel) return;

    const activeItems =
      containsCheckedInputs(qsa('input', panel)) || rangeInputsHaveValue();

    toggle(panel, classes.activeFilters, activeItems);
  }

  function syncActiveStates() {
    const panels = qsa(sel.panel, node);

    panels.forEach(panel => {
      let activeItems = false;
      const rangeInputs = qs('[data-range-input]', panel);

      if (containsCheckedInputs(qsa('input', panel))) {
        activeItems = true;
      }

      if (rangeInputs && rangeInputsHaveValue()) {
        activeItems = true;
      }

      toggle(panel, classes.activeFilters, activeItems);
    });
  }

  function unload() {
    events.forEach(unsubscribe => unsubscribe());
    range && range.unload();
  }

  return {
    unload,
  };
};

export default filterDrawer;
